import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Spinner,
  Table,
} from "reactstrap"
import { Formik, Form, Field, FieldArray } from "formik"
import * as Yup from "yup"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"
import { Spanish } from "flatpickr/dist/l10n/es"

import {
  saveStock,
  getEspeciesGanaderas,
  getCategoriasGanaderas,
  getPreciosHacienda,
  getValoresGanaderia,
} from "api/agro"
import toastHandler from "toastHandler"
import useAuthorization from "hooks/useAuthorization"

const esVacio = v => v === null || v === undefined || v === ""

const numeroEsAR = (v, opts) => {
  if (esVacio(v)) return ""
  const n = Number(v)
  return isNaN(n) ? v : n.toLocaleString("es-AR", opts)
}

const parseNumeroAR = str => {
  if (esVacio(str)) return ""
  const limpio = String(str).replace(/\./g, "").replace(",", ".")
  const n = Number(limpio)
  return isNaN(n) ? "" : n
}

const FORMAT_ENTERO = { maximumFractionDigits: 0 }
const FORMAT_MONTO = { minimumFractionDigits: 2, maximumFractionDigits: 2 }

// Schema - CORREGIDO según StockCreateDTO
const StockSchema = Yup.object().shape({
  idEstablecimiento: Yup.number().required("Seleccione establecimiento"),
  fechaStock: Yup.date().required("Requerido"),
  items: Yup.array()
    .of(
      Yup.object().shape({
        codigoEspecie: Yup.string().required("Requerido"),
        codigoCategoria: Yup.string().required("Requerido"),
        cantidadCabezas: Yup.string()
          .required("Requerido")
          .test("es-numero", "Debe ser un número válido", v =>
            esVacio(v) ? false : !isNaN(parseNumeroAR(v)),
          ),
        cantidadCabezasVenta: Yup.string().test(
          "es-numero",
          "Debe ser un número válido",
          v => esVacio(v) || !isNaN(parseNumeroAR(v)),
        ),
        precioReferenciaKg: Yup.string().test(
          "es-numero",
          "Debe ser un número válido",
          v => esVacio(v) || !isNaN(parseNumeroAR(v)),
        ),
        pesoPromedioKg: Yup.string().test(
          "es-numero",
          "Debe ser un número válido",
          v => esVacio(v) || !isNaN(parseNumeroAR(v)),
        ),
      }),
    )
    .min(1, "Agregue al menos un item"),
})

const FormStock = ({
  establecimientos,
  onSuccess,
  onCancel,
  idEstablecimiento,
}) => {
  // BDSJEA-221 hotfix: ver la nota equivalente en FormSiembra.js/FormStockAgricola.js
  // (BDSJEA-208). Se usa el mismo objeto que ya gatea abrir esta pantalla y
  // Editar/Eliminar en ActividadGanadera.js, en vez del flag global de ruta
  // userStore.modificacion.
  const { puedeModificarObjeto } = useAuthorization()
  const modificacion = puedeModificarObjeto("/agro/establecimientos")
  const [especies, setEspecies] = useState([])
  const [categorias, setCategorias] = useState([])
  const [preciosRef, setPreciosRef] = useState([])
  const [valoresGanaderia, setValoresGanaderia] = useState([])
  const [loading, setLoading] = useState(true)

  const setSuccess = toastHandler(state => state.setSuccess)
  const setError = toastHandler(state => state.setError)

  useEffect(() => {
    loadDominios()
  }, [])

  const loadDominios = async () => {
    try {
      const [especiesData, preciosData, valoresData] = await Promise.all([
        getEspeciesGanaderas(),
        getPreciosHacienda(),
        getValoresGanaderia(),
      ])
      setEspecies(especiesData)
      setPreciosRef(preciosData)
      setValoresGanaderia(valoresData)

      // Cargar categorías de la primera especie por defecto (BOVINO)
      const cats = await getCategoriasGanaderas("BOVINO")
      setCategorias(cats)
    } catch (error) {
      setError("Error al cargar los datos del formulario. Recargá la página.")
    } finally {
      setLoading(false)
    }
  }

  // Cargar categorías cuando cambia la especie
  const handleEspecieChange = async codigoEspecie => {
    if (!codigoEspecie) {
      setCategorias([])
      return
    }
    try {
      const cats = await getCategoriasGanaderas(codigoEspecie)
      setCategorias(cats)
    } catch (error) {
      setError("Error al cargar las categorías. Intentá de nuevo.")
      setCategorias([])
    }
  }

  // Obtener precio de referencia por categoría
  const getPrecioReferencia = codigoCategoria => {
    const valores = getValoresPorCategoria(codigoCategoria)
    if (valores?.precioKilo) return valores.precioKilo
    const precio = preciosRef.find(p =>
      p.categoria?.toLowerCase().includes(codigoCategoria?.toLowerCase()),
    )
    return precio?.precioPromedio || null
  }

  // Obtener valores de ganadería (peso_venta, precio_kilo) por código de categoría
  const getValoresPorCategoria = codigoCategoria => {
    return (
      valoresGanaderia.find(v => v.codigoCategoria === codigoCategoria) || null
    )
  }

  // Calcular valuación
  const calcularValuacion = (cantidadCabezas, pesoPromedioKg, precioKg) => {
    const peso = parseNumeroAR(pesoPromedioKg)
    if (!cantidadCabezas || !peso || !precioKg) return null
    return cantidadCabezas * peso * precioKg
  }

  // Guardar
  const handleSubmit = async (values, { setSubmitting, setTouched }) => {
    // Validar campos requeridos y marcar como touched
    const newTouched = {
      idEstablecimiento: true,
      fechaStock: true,
    }
    setTouched(newTouched)

    // Si faltan campos requeridos, no enviar
    if (!values.idEstablecimiento || !values.fechaStock) {
      setSubmitting(false)
      return
    }

    console.log(values)
    try {
      // Guardar cada item del stock
      for (const item of values.items) {
        await saveStock({
          idEstablecimiento: parseInt(values.idEstablecimiento),
          codigoEspecie: item.codigoEspecie,
          codigoCategoria: item.codigoCategoria,
          cantidadCabezas: parseNumeroAR(item.cantidadCabezas),
          cantidadCabezasVenta: item.cantidadCabezasVenta
            ? parseNumeroAR(item.cantidadCabezasVenta)
            : parseNumeroAR(item.cantidadCabezas),
          pesoPromedioKg: item.pesoPromedioKg
            ? parseNumeroAR(item.pesoPromedioKg)
            : null,
          precioReferenciaKg: item.precioReferenciaKg
            ? parseNumeroAR(item.precioReferenciaKg)
            : null,
          fechaStock: values.fechaStock,
        })
      }

      setSuccess("Stock registrado correctamente")
      onSuccess && onSuccess()
    } catch (error) {
      setError("Error al guardar: " + error.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner color="primary" />
      </div>
    )
  }

  return (
    <Formik
      initialValues={{
        idEstablecimiento: idEstablecimiento || "",
        fechaStock: new Date(),
        items: [
          {
            codigoEspecie: "BOVINO",
            codigoCategoria: "",
            cantidadCabezas: "",
            cantidadCabezasVenta: "",
            precioReferenciaKg: "",
            pesoPromedioKg: "",
          },
        ],
      }}
      validationSchema={StockSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <Form>
          <Row className="mb-4">
            <Col md={6}>
              <FormGroup>
                <Label>Establecimiento *</Label>
                <Field name="idEstablecimiento">
                  {({ field, form }) => (
                    <Input
                      {...field}
                      type="select"
                      invalid={
                        form.touched.idEstablecimiento &&
                        !form.values.idEstablecimiento
                      }
                    >
                      <option value="">Seleccione...</option>
                      {establecimientos.map(est => (
                        <option key={est.id} value={est.id}>
                          {est.nombre}
                        </option>
                      ))}
                    </Input>
                  )}
                </Field>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Fecha de Stock *</Label>
                <Flatpickr
                  className={`form-control ${
                    touched.fechaStock && !values.fechaStock ? "is-invalid" : ""
                  }`}
                  value={values.fechaStock}
                  onChange={([date]) => setFieldValue("fechaStock", date)}
                  options={{ dateFormat: "d/m/Y", locale: Spanish }}
                />
              </FormGroup>
            </Col>
          </Row>

          <h6 className="mb-3">Detalle de Stock</h6>

          <FieldArray name="items">
            {({ push, remove }) => (
              <>
                <Table responsive bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th colSpan="2" className="text-center">
                        Unidad
                      </th>
                      <th width="110" className="text-center">
                        Peso Promedio
                      </th>
                      <th width="120" className="text-center">
                        Precio Kg
                      </th>
                      <th colSpan="2" className="text-center">
                        Cabezas
                      </th>
                      <th width="140" className="text-center">
                        Valuación
                      </th>
                      <th width="60" className="text-center"></th>
                    </tr>
                    <tr>
                      <th width="130" className="text-center">
                        Especie
                      </th>
                      <th width="150" className="text-center">
                        Categoría
                      </th>
                      <th className="text-center">(kg)</th>
                      <th className="text-center">(USD/kg)</th>
                      <th width="80" className="text-center">
                        Totales
                      </th>
                      <th width="80" className="text-center">
                        Para venta
                      </th>
                      <th className="text-center">Estimada</th>
                      <th width="60" className="text-center"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {values.items.map((item, index) => {
                      const cabezasParaVenta = item.cantidadCabezasVenta
                        ? parseNumeroAR(item.cantidadCabezasVenta)
                        : parseNumeroAR(item.cantidadCabezas) || 0
                      const valuacion = calcularValuacion(
                        cabezasParaVenta,
                        item.pesoPromedioKg,
                        parseNumeroAR(item.precioReferenciaKg),
                      )

                      return (
                        <tr key={index}>
                          <td>
                            <Input
                              name="codigoEspecie"
                              type="select"
                              bsSize="sm"
                              value={item.codigoEspecie}
                              onChange={e => {
                                setFieldValue(
                                  `items.${index}.codigoEspecie`,
                                  e.target.value,
                                )
                                setFieldValue(
                                  `items.${index}.codigoCategoria`,
                                  "",
                                )
                                handleEspecieChange(e.target.value)
                              }}
                            >
                              <option value="">Seleccione...</option>
                              {especies.map(esp => (
                                <option key={esp.codigo} value={esp.codigo}>
                                  {esp.descripcion}
                                </option>
                              ))}
                            </Input>
                          </td>
                          <td>
                            <Field
                              name={`items.${index}.codigoCategoria`}
                              as={Input}
                              type="select"
                              bsSize="sm"
                              onChange={e => {
                                const codigoCat = e.target.value
                                setFieldValue(
                                  `items.${index}.codigoCategoria`,
                                  codigoCat,
                                )
                                // Autocompletar pesoPromedioKg y precioReferenciaKg desde valoresGanaderia
                                const valores =
                                  getValoresPorCategoria(codigoCat)
                                if (valores) {
                                  if (valores.pesoVenta) {
                                    setFieldValue(
                                      `items.${index}.pesoPromedioKg`,
                                      numeroEsAR(
                                        valores.pesoVenta,
                                        FORMAT_MONTO,
                                      ),
                                    )
                                  }
                                  if (valores.precioKilo) {
                                    setFieldValue(
                                      `items.${index}.precioReferenciaKg`,
                                      numeroEsAR(
                                        valores.precioKilo,
                                        FORMAT_MONTO,
                                      ),
                                    )
                                  }
                                } else if (codigoCat) {
                                  setError(
                                    "No hay valores de referencia para la categoría seleccionada. Ingresá el peso promedio manualmente.",
                                  )
                                }
                              }}
                            >
                              <option value="">Seleccione...</option>
                              {categorias.map(cat => (
                                <option key={cat.codigo} value={cat.codigo}>
                                  {cat.descripcion}
                                </option>
                              ))}
                            </Field>
                          </td>
                          <td className="text-end">
                            <Field
                              name={`items.${index}.pesoPromedioKg`}
                              as={Input}
                              type="text"
                              bsSize="sm"
                              className="text-end"
                              onBlur={e => {
                                const val = parseNumeroAR(e.target.value)
                                if (val !== "") {
                                  setFieldValue(
                                    `items.${index}.pesoPromedioKg`,
                                    numeroEsAR(val, FORMAT_MONTO),
                                  )
                                }
                              }}
                            />
                          </td>
                          <td className="text-end">
                            <Field
                              name={`items.${index}.precioReferenciaKg`}
                              as={Input}
                              type="text"
                              bsSize="sm"
                              className="text-end"
                              onBlur={e => {
                                const val = parseNumeroAR(e.target.value)
                                if (val !== "") {
                                  setFieldValue(
                                    `items.${index}.precioReferenciaKg`,
                                    numeroEsAR(val, FORMAT_MONTO),
                                  )
                                }
                              }}
                            />
                          </td>
                          <td className="text-end">
                            <Field
                              name={`items.${index}.cantidadCabezas`}
                              as={Input}
                              type="text"
                              bsSize="sm"
                              className="text-end"
                              min="1"
                              onBlur={e => {
                                const val = parseNumeroAR(e.target.value)
                                if (val !== "") {
                                  setFieldValue(
                                    `items.${index}.cantidadCabezas`,
                                    numeroEsAR(val, FORMAT_ENTERO),
                                  )
                                }
                              }}
                            />
                          </td>
                          <td className="text-end">
                            <Field
                              name={`items.${index}.cantidadCabezasVenta`}
                              as={Input}
                              type="text"
                              bsSize="sm"
                              className="text-end"
                              min="0"
                              placeholder={item.cantidadCabezas || ""}
                              onBlur={e => {
                                const val = parseNumeroAR(e.target.value)
                                if (val !== "") {
                                  setFieldValue(
                                    `items.${index}.cantidadCabezasVenta`,
                                    numeroEsAR(val, FORMAT_ENTERO),
                                  )
                                }
                              }}
                            />
                          </td>
                          <td className="text-end">
                            {valuacion ? (
                              <strong className="text-success">
                                USD{" "}
                                {valuacion.toLocaleString("es-AR", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </strong>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td
                            className="text-center"
                            style={{
                              width: 50,
                              minWidth: 50,
                              maxWidth: 50,
                              verticalAlign: "middle",
                            }}
                          >
                            {values.items.length > 1 && (
                              <Button
                                color="danger"
                                size="sm"
                                outline
                                style={{ width: "100%", padding: "0.2rem" }}
                                onClick={() => remove(index)}
                              >
                                <i className="mdi mdi-trash-can" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="table-light">
                      <td colSpan={2}>
                        <Button
                          color="success"
                          size="sm"
                          outline
                          onClick={() =>
                            push({
                              codigoEspecie: "BOVINO",
                              codigoCategoria: "",
                              cantidadCabezas: "",
                              cantidadCabezasVenta: "",
                              pesoPromedioKg: "",
                              precioReferenciaKg: "",
                            })
                          }
                        >
                          <i className="mdi mdi-plus me-1" />
                          Agregar Línea
                        </Button>
                      </td>
                      <td className="text-end">
                        <strong>
                          {values.items
                            .reduce(
                              (acc, i) =>
                                acc + (parseNumeroAR(i.cantidadCabezas) || 0),
                              0,
                            )
                            .toLocaleString("es-AR")}
                        </strong>
                      </td>
                      <td className="text-end">
                        <strong>
                          {values.items
                            .reduce((acc, i) => {
                              const venta = i.cantidadCabezasVenta
                                ? parseNumeroAR(i.cantidadCabezasVenta)
                                : parseNumeroAR(i.cantidadCabezas) || 0
                              return acc + venta
                            }, 0)
                            .toLocaleString("es-AR")}
                        </strong>
                      </td>
                      <td colSpan={2}></td>
                      <td className="text-end">
                        <strong>
                          USD{" "}
                          {values.items
                            .reduce((acc, i) => {
                              const cabezasParaVenta = i.cantidadCabezasVenta
                                ? parseNumeroAR(i.cantidadCabezasVenta)
                                : parseNumeroAR(i.cantidadCabezas) || 0
                              const val = calcularValuacion(
                                cabezasParaVenta,
                                i.pesoPromedioKg,
                                parseNumeroAR(i.precioReferenciaKg),
                              )
                              return acc + (val || 0)
                            }, 0)
                            .toLocaleString("es-AR", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                        </strong>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </>
            )}
          </FieldArray>

          {errors.items && typeof errors.items === "string" && (
            <div className="text-danger small mb-3">{errors.items}</div>
          )}

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button color="secondary" onClick={onCancel}>
              Cancelar
            </Button>
            {modificacion && (
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Spinner size="sm" /> : "Guardar Stock"}
              </Button>
            )}
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default FormStock
